/**
 * Client-side persistence of the chosen auth/backend environment.
 *
 * The selection must survive reloads so token refresh keeps using the same
 * Cognito pool the user logged into. Stored in localStorage; falls back to the
 * default environment when unset or running server-side (no window).
 */
import {
  type AuthEnvironment,
  getDefaultEnvironment,
  getEnvironmentByKey,
} from "./auth-environments";

const STORAGE_KEY = "authEnvKey";

export function getSelectedEnvKey(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setSelectedEnvKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // Ignore storage failures (private mode, quota); fall back to default.
  }
}

/** The currently selected environment, or the default when none is stored. */
export function getSelectedEnvironment(): AuthEnvironment {
  const key = getSelectedEnvKey();
  return (
    (key ? getEnvironmentByKey(key) : undefined) ?? getDefaultEnvironment()
  );
}
